import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import { FaSpinner, FaCalendar, FaUser, FaArrowLeft, FaFacebookF, FaTwitter, FaWhatsapp, FaEnvelope, FaEye } from 'react-icons/fa';
import './MediaDetail.css';

const FALLBACK_MEDIA = [
  {
    _id: "1",
    title: "5 Tips for Finding the Best Cab Service",
    description: "Learn how to choose the right cab service for your daily commute. We share insider tips on what makes a great ride experience. Finding a reliable cab service can transform your daily travel routine from a stressful chore into a relaxing, productive journey. Consider checking vehicle condition, checking ratings, and comparing price tiers before you book.",
    slug: "5-tips-best-cab-service",
    category: "Travel Tips",
    featuredImage: "https://images.unsplash.com/photo-1500595046891-13d9a48c2c44?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Rajesh Kumar",
    authorImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-15",
    viewCount: 120,
    content: `<h2>Transform Your Commute</h2>
<p>Finding a reliable cab service can transform your daily travel routine from a stressful chore into a relaxing, productive journey. Whether you are commuting to work, heading to an important business meeting, or catching a flight, the quality of your ride matters.</p>
<h3>1. Prioritize Safety and Driver Background Checks</h3>
<p>Always choose a service that implements strict safety guidelines, including verified driver background checks and active GPS monitoring of all trips. Knowing that your driver has been thoroughly vetted gives you peace of mind.</p>
<blockquote>"Safety isn't an option; it's a fundamental requirement of modern mobility."</blockquote>
<h3>2. Review Vehicle Conditions and Options</h3>
<p>A great service offers a well-maintained fleet with various categories to suit your needs, from economic sedans to luxury vehicles. Make sure the service you choose has high standards for vehicle cleanliness and upkeep.</p>
<h3>3. Look for Transparent Pricing</h3>
<p>Avoid services with hidden fees or unpredictable surge pricing models. Transparent pricing with upfront fare estimates lets you plan your budget without any nasty surprises at the end of the trip.</p>`
  },
  {
    _id: "2",
    title: "Safety First: Your Guide to Secure Rides",
    description: "Safety is our top priority. Discover the comprehensive safety measures in place to protect you on every journey. From mandatory background checks for all drivers to real-time GPS tracking and emergency assistance features built into our app, we make sure that your ride with CabZigo is as secure as possible.",
    slug: "safety-first-secure-rides",
    category: "Safety",
    featuredImage: "https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Priya Sharma",
    authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-20",
    viewCount: 340,
    content: `<h2>Your Safety is Our Commitment</h2>
<p>Safety is the cornerstone of everything we do at CabZigo. We understand that when you book a ride, you place your trust in us. That is why we have engineered multi-layered security protocols to safeguard every journey.</p>
<h3>Rigorous Driver Vetting</h3>
<p>Every driver partner undergoes comprehensive criminal background checks and professional driving history verifications before joining our network. We also conduct ongoing performance evaluations and safety training modules.</p>
<blockquote>"We believe every passenger deserves to feel absolutely secure, from pickup to drop-off."</blockquote>
<h3>Advanced Technology Features</h3>
<ul>
  <li><strong>Real-Time GPS Tracking:</strong> Your ride is monitored 24/7 by our security team, and you can share your live location with friends or family.</li>
  <li><strong>SOS Emergency Button:</strong> Instant connection to our emergency helpline and local authorities with a single tap in the app.</li>
  <li><strong>Masked Contact Numbers:</strong> We protect your privacy by routing calls through anonymous relays, keeping your phone number hidden.</li>
</ul>`
  },
  {
    _id: "3",
    title: "How to Save Money on Daily Commutes",
    description: "Discover smart ways to save on your daily rides. Our money-saving tips can help you cut commute costs significantly. Booking during off-peak times, keeping an eye out for dynamic discount codes, utilizing our wallet bonuses, or referring your friends to earn points are excellent strategies to travel comfortably while staying within your budget.",
    slug: "save-money-daily-commutes",
    category: "Budget Tips",
    featuredImage: "https://images.unsplash.com/photo-1566776615325-7c321b0b5a7d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Amit Patel",
    authorImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-22",
    viewCount: 215,
    content: `<h2>Smart Travel, Better Savings</h2>
<p>Daily commuting costs can add up quickly. However, with a few strategic adjustments, you can enjoy premium ride-hailing services without breaking the bank. Here are the top ways to save on your commutes.</p>
<h3>1. Take Advantage of Off-Peak Timing</h3>
<p>If your schedule permits, travel during off-peak hours when demand is lower. You will save money on fares and enjoy quicker transit times through lighter traffic.</p>
<h3>2. Leverage Wallet and Referral Bonuses</h3>
<p>Using the pre-loaded digital wallet often grants cashback bonuses. Additionally, sharing your unique referral link with friends and colleagues rewards both of you with significant travel discounts.</p>
<blockquote>"By planning ahead and using built-in loyalty perks, savvy riders can reduce their monthly transport bills by up to 25%."</blockquote>
<h3>3. Book Shared Packages and Outstation Deals</h3>
<p>For longer journeys, explore flat-rate packages and hourly rental plans instead of multiple single-trip bookings. Outstation round-trips also offer competitive pricing structures compared to one-way hires.</p>`
  },
  {
    _id: "4",
    title: "CabZigo Expands to 10 New Cities",
    description: "We are thrilled to announce our expansion into 10 more cities across India, bringing comfortable travel to even more people. This milestone expansion is a testament to our users' trust. Our operations in these cities will feature the same high standard of driver professionalism, safety protocols, and transparent pricing models.",
    slug: "cabzigo-expands-10-new-cities",
    category: "News",
    featuredImage: "https://images.unsplash.com/photo-1517733994350-d41e7ceeb115?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90",
    authorName: "Karan Singh",
    authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-02-01",
    viewCount: 450,
    content: `<h2>Connecting More People, More Cities</h2>
<p>We are thrilled to officially announce that CabZigo is expanding its premium, reliable ride-hailing services to 10 additional major cities across India. This mark of growth is driven by the overwhelming support and trust of our passengers.</p>
<blockquote>"Our goal is to make professional, safe, and transparent transportation accessible to everyone, everywhere."</blockquote>
<h3>Bringing the CabZigo Standard to New Regions</h3>
<p>Each new city will have access to our entire product suite, including one-way airport transfers, hourly rentals, and outstation rides. We are setting up local driver training hubs to ensure that our signature standard of quality and safety is maintained consistently.</p>
<p>The new cities included in this expansion phase will roll out services starting this week. Keep an eye on your app for local introductory promos and ride discounts!</p>`
  },
  {
    _id: "5",
    title: "Eco-Friendly Rides: Our New Electric Fleet",
    description: "Join us in our journey towards sustainability. CabZigo introduces a brand new fleet of electric vehicles for a greener future. Reducing our carbon footprint is one of our primary goals this decade. The new electric cabs provide a whisper-quiet, emission-free ride, letting passengers choose sustainable travel choices at no extra cost.",
    slug: "eco-friendly-electric-fleet",
    category: "Sustainability",
    featuredImage: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90",
    authorName: "Neha Sharma",
    authorImage: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-02-10",
    viewCount: 520,
    content: `<h2>Steering Towards a Cleaner Future</h2>
<p>Environmental sustainability is no longer a choice—it is a responsibility. CabZigo is proud to introduce our brand-new fleet of fully electric vehicles (EVs), marking a key milestone in our mission to green urban mobility.</p>
<h3>Why Go Electric with CabZigo?</h3>
<ul>
  <li><strong>Zero Tailpipe Emissions:</strong> Every kilometer driven in our electric fleet prevents greenhouse gases from polluting our city air.</li>
  <li><strong>Whisper-Quiet Comfort:</strong> Enjoy a peaceful, noise-free ride that helps reduce urban noise pollution.</li>
  <li><strong>Same Great Rates:</strong> We believe green choices shouldn't be expensive. Ride in our EVs at the exact same pricing as standard vehicles.</li>
</ul>
<blockquote>"Our commitment to the environment means transitioning 50% of our active fleet to electric power over the next three years."</blockquote>
<p>The next time you book a ride on the CabZigo app, look for the green leaf icon to select an eco-friendly ride and do your part for the planet!</p>`
  }
];

const MediaDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [mediaItem, setMediaItem] = useState(null);
  const [relatedMedia, setRelatedMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMediaDetail();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchMediaDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if it matches fallback slug or numerical ID first to avoid failing lookup requests
      const fallbackItem = FALLBACK_MEDIA.find(item => item.slug === slug || item._id === String(slug));
      
      if (fallbackItem) {
        setMediaItem(fallbackItem);
        // Load some related articles from remaining fallbacks
        const remaining = FALLBACK_MEDIA.filter(item => item.slug !== slug && item._id !== String(slug)).slice(0, 3);
        setRelatedMedia(remaining);
        setLoading(false);
        return;
      }

      // Try fetching from backend API (endpoint expects id or slug)
      const response = await axios.get(`${API_URL}/media/public/${slug}`);
      
      if (response.data.success && response.data.data) {
        const fetchedMedia = response.data.data;
        setMediaItem(fetchedMedia);
        
        // If they visited using the DB ID in URL, redirect to the clean slug URL instead!
        if (slug === fetchedMedia._id && fetchedMedia.slug) {
          navigate(`/media/${fetchedMedia.slug}`, { replace: true });
        }

        // Fetch other public media for the related sidebar
        try {
          const relatedResponse = await axios.get(`${API_URL}/media/public`, { params: { limit: 4 } });
          if (relatedResponse.data.success && relatedResponse.data.data) {
            const filtered = relatedResponse.data.data.filter(
              item => item._id !== fetchedMedia._id && item.slug !== fetchedMedia.slug
            ).slice(0, 3);
            setRelatedMedia(filtered);
          }
        } catch (relatedErr) {
          console.warn("Could not load related media:", relatedErr);
        }
      } else {
        setError('Media post not found');
      }
    } catch (err) {
      console.error('Error fetching media detail:', err);
      // Attempt fallback lookup on backend network error to maintain a working demo page
      const matchedFallback = FALLBACK_MEDIA.find(item => item.slug === slug || item._id === String(slug)) || FALLBACK_MEDIA[0];
      if (matchedFallback) {
        setMediaItem(matchedFallback);
        setRelatedMedia(FALLBACK_MEDIA.filter(item => item.slug !== matchedFallback.slug).slice(0, 3));
      } else {
        setError(err.response?.data?.message || 'Failed to load media post');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRelatedClick = (relItem) => {
    navigate(`/media/${relItem.slug || relItem._id}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-slate-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-blue-600 text-5xl mx-auto mb-4" />
          <p className="text-gray-500 font-bold text-sm tracking-wider uppercase">Loading Story...</p>
        </div>
      </div>
    );
  }

  if (error || !mediaItem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-md bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
          <h1 className="text-3xl font-black text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-500 mb-8">{error || 'This media post could not be found.'}</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-full font-bold hover:shadow-lg transition-all hover:scale-[1.02]">
            <FaArrowLeft /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <div className="media-detail-container bg-slate-50 pt-12 pb-16 md:pt-12 md:pb-28">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumbs */}
        <nav className="mb-8">
          <Link to="/#media-section" className="inline-flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest hover:text-blue-600 transition-colors duration-200">
            <FaArrowLeft className="text-[10px]" /> Back to Homepage
          </Link>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* Main Content */}
          <main className="lg:col-span-8 bg-white rounded-3xl p-6 sm:p-10 shadow-sm border border-gray-100">
            
            {/* Header info */}
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <span className="bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest px-3.5 py-1.5 rounded-lg">
                  {mediaItem.category}
                </span>
                <span className="text-gray-400 text-xs font-bold flex items-center gap-1.5">
                  <FaEye className="text-gray-400" /> {mediaItem.viewCount || 0} Views
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                {mediaItem.title}
              </h1>

              {/* Author & Date Card */}
              <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                <img 
                  src={mediaItem.authorImage} 
                  alt={mediaItem.authorName} 
                  className="w-12 h-12 rounded-full object-cover border border-gray-100" 
                  onError={(e) => {
                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3EAuthor%3C/text%3E%3C/svg%3E";
                  }}
                />
                <div>
                  <p className="text-xs font-black text-gray-900 uppercase tracking-wider">{mediaItem.authorName}</p>
                  <p className="text-xs font-bold text-gray-400 mt-0.5">
                    Published {new Date(mediaItem.publishDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            <div className="rounded-2xl overflow-hidden mb-8 md:mb-10 shadow-sm border border-gray-100 bg-gray-50">
              <img 
                src={mediaItem.featuredImage} 
                alt={mediaItem.title} 
                className="w-full h-auto object-cover max-h-[500px]" 
                onError={(e) => {
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='24' fill='%236b7280'%3EMedia Image%3C/text%3E%3C/svg%3E";
                }}
              />
            </div>

            {/* Main Text Content */}
            <article className="prose max-w-none">
              {mediaItem.content ? (
                <div 
                  className="text-gray-700 leading-relaxed font-medium space-y-6 rich-media-content"
                  dangerouslySetInnerHTML={{ __html: mediaItem.content }} 
                />
              ) : (
                <p className="text-lg md:text-xl font-medium text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {mediaItem.description}
                </p>
              )}
            </article>

          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-8">
            
            {/* Share Card */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100">
              <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest mb-6">Share Story</h3>
              <div className="flex gap-4">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 bg-slate-50 hover:bg-blue-600 hover:text-white rounded-full flex items-center justify-center text-gray-700 transition-all duration-300 shadow-sm"
                  title="Share on Facebook"
                >
                  <FaFacebookF size={14} />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(mediaItem.title)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 bg-slate-50 hover:bg-blue-400 hover:text-white rounded-full flex items-center justify-center text-gray-700 transition-all duration-300 shadow-sm"
                  title="Share on Twitter"
                >
                  <FaTwitter size={14} />
                </a>
                <a 
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(mediaItem.title + " - " + shareUrl)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 bg-slate-50 hover:bg-green-500 hover:text-white rounded-full flex items-center justify-center text-gray-700 transition-all duration-300 shadow-sm"
                  title="Share on WhatsApp"
                >
                  <FaWhatsapp size={14} />
                </a>
                <a 
                  href={`mailto:?subject=${encodeURIComponent(mediaItem.title)}&body=${encodeURIComponent("Check out this article: " + shareUrl)}`} 
                  className="w-10 h-10 bg-slate-50 hover:bg-red-500 hover:text-white rounded-full flex items-center justify-center text-gray-700 transition-all duration-300 shadow-sm"
                  title="Share via Email"
                >
                  <FaEnvelope size={14} />
                </a>
              </div>
            </div>

            {/* Related Media Section */}
            {relatedMedia.length > 0 && (
              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100">
                <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest mb-6">More Stories</h3>
                <div className="space-y-6">
                  {relatedMedia.map((relItem) => (
                    <div 
                      key={relItem._id} 
                      className="group flex gap-4 cursor-pointer items-start"
                      onClick={() => handleRelatedClick(relItem)}
                    >
                      <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden shadow-sm border border-gray-50 bg-slate-50">
                        <img 
                          src={relItem.featuredImage} 
                          alt={relItem.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3C/svg%3E";
                          }}
                        />
                      </div>
                      <div className="flex-grow pt-1">
                        <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest block mb-1">
                          {relItem.category}
                        </span>
                        <h4 className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug group-hover:text-blue-600 transition-colors duration-200">
                          {relItem.title}
                        </h4>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </aside>
        </div>

      </div>
    </div>
  );
};

export default MediaDetail;
